import React, { useContext } from 'react'
import { useFormikContext } from 'formik'
import Box from '@material-ui/core/Box'
import map from 'lodash/map'
import get from 'lodash/get'

import { Grid } from '@components/layout/Grid'
import { InfoSection } from '@components/InfoSection'
import bannerImg from '@assets/images/general_banner_img.png'
import { Button } from '@components/Button'
import { CardFooter } from '@components/layout/Card'
import { LiabilityCard, } from '@components/Liabilities/Card'
import { RiskProductCard } from '@components/RiskProducts/Card'
import { AssetCard } from '@components/Assets/card'

import { ASIDE_VIEWS, ASSESSMENT_TYPE } from '../../reducer'
import { FnaContext } from '../../context'
import { sumSelectedLiabilities, sumSelectedRiskProducts, sumSelectedAssetsContributions, sumSelectedAssets } from '../../utils'

export const Aside = ({ asideView }) => {
  if (!asideView) {
    return <DefaultAside />
  }

  switch (asideView) {
    case ASIDE_VIEWS.ESTATE_DUTY:
      return <AsideInfo
        title='Estate Duty'
        Content={() =>
          <>
            <p>A basic estate duty calculation is performed on all the assets in the scenario and not only on the ones that are included in the life cover assessment.</p>
            <p>The calculation excludes all retirement funding assets such as retirement annuities, pensions, etc. The assumption is that those assets have specific beneficiaries nominated.</p>
          </>
        }
      />
    case ASIDE_VIEWS.DEPENDENTS:
      return <AsideInfo
        title='Monthly income for dependents'
        Content={() =>
          <>
            <p>It can sometimes be tricky to relate large lump sum amounts to real-life needs. For example, some people might think that if they have 3 million in life cover, for their dependents, they might be able to drop that down to 2 million to save on the premium, because 2 million still sounds like a lot, right?</p>
            <p>Well, consider that that reduction reduces the effective income from 25 000 to only about 16 500 per month for their dependents when taken over 10 years.</p>
            <p>So we've framed the dependent values as monthly values here to make it easier to reason about how much is reasonable from a monthly point of view.</p>
          </>
        }
      />
    case ASIDE_VIEWS.ASSETS:
      return <Selections asideView={asideView} />
    case ASIDE_VIEWS.LIABILITIES:
      return <Selections asideView={asideView} />
    case ASIDE_VIEWS.RISK_PRODUCTS:
      return <Selections asideView={asideView} />
    default:
      return <DefaultAside />
  }

}

const Selections = ({ asideView }) => {
  const onSelectAll = getOnSelectAllByAsideView(asideView)

  return (
    <Box>
      <Box
        className='flex justify-between items-start flex-col'
      >
        <Header asideView={asideView} />
        <Box>
          <Button className='text-link'
            onClick={onSelectAll}>
            Select all
          </Button>
        </Box>
      </Box>
      <Cards asideView={asideView} />
    </Box>
  )
}

const Cards = ({ asideView }) => {
  switch (asideView) {
    case ASIDE_VIEWS.ASSETS:
      return <AssetCards />
    case ASIDE_VIEWS.LIABILITIES:
      return <LiabilityCards />
    case ASIDE_VIEWS.RISK_PRODUCTS:
      return <RiskProductCards />
    default:
      return null
  }
}

const Header = ({ asideView }) => {
  switch (asideView) {
    case ASIDE_VIEWS.ASSETS:
      return () => <h2>Select assets</h2>
    case ASIDE_VIEWS.LIABILITIES:
      return () => <h2>Select liabilities</h2>
    case ASIDE_VIEWS.RISK_PRODUCTS:
      return () => <h2>Select risk products</h2>
    default:
      return null
  }
}

const getOnSelectAllByAsideView = asideView => {
  const {
    state: { liabilities, riskProducts, assets, supportedCurrencies, assessmentType },
  } = useContext(FnaContext)
  const {
    setFieldValue,
    values: {
      liabilitiables_attributes,
      risk_productables_attributes,
      assetables_attributes
    }
  } = useFormikContext()


  switch (asideView) {
    case ASIDE_VIEWS.LIABILITIES:
      return () => {
        const newLiabilitiablesAttributes = liabilities.map(liability => ({
          id: get(liabilitiables_attributes.find(liabilitiable => liabilitiable.liability_id === liability.id), 'id', null),
          liability_id: liability.id,
          _destroy: false
        }))

        setFieldValue('liabilitiables_attributes', newLiabilitiablesAttributes)
        setFieldValue('total_liabilities_expenses', sumSelectedLiabilities(liabilities, newLiabilitiablesAttributes, [], []))
        setFieldValue('total_from_selected_liabilities', sumSelectedLiabilities(liabilities, newLiabilitiablesAttributes, assets, assetables_attributes))
      }
    case ASIDE_VIEWS.RISK_PRODUCTS:
      const compatibleRiskProductsForAssessment = getCompatibleRiskProductsForAssessment(assessmentType)
      const riskProductForAssessment = riskProduct => compatibleRiskProductsForAssessment.includes(riskProduct.product_type)
      return () => {
        const newRiskProductableAttributes = riskProducts.filter(riskProductForAssessment).map(riskProduct => ({
          id: get(risk_productables_attributes.find(riskProductable => riskProductable.risk_product_id === riskProduct.id), 'id', null),
          risk_product_id: riskProduct.id,
          _destroy: false
        }))

        setFieldValue('risk_productables_attributes', newRiskProductableAttributes)
        setFieldValue('total_from_selected_risk_products', sumSelectedRiskProducts(riskProducts, newRiskProductableAttributes))
      }
    case ASIDE_VIEWS.ASSETS:
      return () => {
        const newAssetableAttributes = assets.map(asset => ({
          id: get(assetables_attributes.find(assetable => assetable.asset_id === asset.id), 'id', null),
          asset_id: asset.id,
          _destroy: false
        }))

        setFieldValue('assetables_attributes', newAssetableAttributes)
        setFieldValue('total_asset_monthly_contributions', sumSelectedAssetsContributions(assets, newAssetableAttributes, supportedCurrencies))
        setFieldValue('total_from_selected_assets', sumSelectedAssets(assets, newAssetableAttributes, supportedCurrencies))
        setFieldValue('total_from_selected_liabilities', sumSelectedLiabilities(liabilities, liabilitiables_attributes, assets, newAssetableAttributes))
      }

    default:
      return null
  }
}

const LiabilityCards = () => {
  const {
    state: { liabilities, assets },
  } = useContext(FnaContext)
  const {
    setFieldValue,
    values: {
      liabilitiables_attributes,
      assetables_attributes
    }
  } = useFormikContext()

  const onSelectLiability = (liabilityId, selected) => () => {
    const newLiabilitiablesAttributes = [
      ...liabilitiables_attributes.filter(liabilitiable => liabilitiable.liability_id !== liabilityId),
      {
        id: get(liabilitiables_attributes.find(liabilitiable => liabilitiable.liability_id === liabilityId), 'id', null),
        liability_id: liabilityId,
        _destroy: selected
      }
    ]
    setFieldValue('liabilitiables_attributes', newLiabilitiablesAttributes)
    setFieldValue('total_liabilities_expenses', sumSelectedLiabilities(liabilities, newLiabilitiablesAttributes, assets, assetables_attributes))
    setFieldValue('total_from_selected_liabilities', sumSelectedLiabilities(liabilities, newLiabilitiablesAttributes, assets, assetables_attributes))
  }
  return (
    <Grid mediumColumns>
      {map(liabilities, (liability, key) => {
        const selected = liabilityExistsInLiabilitiables(liability, liabilitiables_attributes)
        return (
          <LiabilityCard
            highlighted={selected}
            key={key}
            liability={liability}
            Footer={() =>
              <CardSelectionFooter
                onClick={onSelectLiability(liability.id, selected)}
                trackAs={'Fna: Select liability'}
                selected={selected}
              />
            }
          />
        )
      })}
    </Grid>
  )
}

const RiskProductCards = () => {
  const {
    state: { riskProducts, assessmentType },
  } = useContext(FnaContext)
  const {
    setFieldValue,
    values: {
      risk_productables_attributes
    }
  } = useFormikContext()

  const onSelectRiskProduct = (riskProductId, selected) => () => {
    const newRiskProductablesAttributes = [
      ...risk_productables_attributes.filter(riskProductable => riskProductable.risk_product_id !== riskProductId),
      {
        id: get(risk_productables_attributes.find(riskProductable => riskProductable.risk_product_id === riskProductId), 'id', null),
        risk_product_id: riskProductId,
        _destroy: selected
      }
    ]
    setFieldValue('risk_productables_attributes', newRiskProductablesAttributes)
    setFieldValue('total_from_selected_risk_products', sumSelectedRiskProducts(riskProducts, newRiskProductablesAttributes))
  }

  const compatibleRiskProductsForAssessment = getCompatibleRiskProductsForAssessment(assessmentType)
  const riskProductIsForAssessment = riskProduct => compatibleRiskProductsForAssessment.includes(riskProduct.product_type)
  const riskProductsForAssessment = riskProducts.filter(riskProductIsForAssessment)
  const riskProductsExist = riskProductsForAssessment.length > 0

  return (
    <>
      {!riskProductsExist ? <p><strong>You do not have any supported risk products for this assessment</strong></p> : null}
      <Grid mediumColumns>
        {map(riskProductsForAssessment, (riskProduct, key) => {
          const selected = riskProductExistsInRiskProductables(riskProduct, risk_productables_attributes)
          return (
            <RiskProductCard
              highlighted={selected}
              key={key}
              riskProduct={riskProduct}
              Footer={() =>
                <CardSelectionFooter
                  onClick={onSelectRiskProduct(riskProduct.id, selected)}
                  trackAs={'Fna: Select risk product'}
                  selected={selected}
                />
              }
            />
          )
        })}
      </Grid>
    </>
  )
}

const getCompatibleRiskProductsForAssessment = (assessmentType) => {
  switch (assessmentType) {
    case ASSESSMENT_TYPE.INCOME_PROTECTION:
      return ['income_protection', 'other_policy']
    case ASSESSMENT_TYPE.DISABILITY_COVER:
      return ['disability_cover', 'other_policy']
    case ASSESSMENT_TYPE.LIFE_COVER:
      return ['life_policy', 'other_policy']
  }
}

const AssetCards = () => {
  const {
    state: { assets, liabilities, supportedCurrencies },
  } = useContext(FnaContext)
  const {
    setFieldValue,
    values: {
      assetables_attributes,
      liabilitiables_attributes
    }
  } = useFormikContext()

  const onSelectAsset = (assetId, selected) => () => {
    const newAssetableAttributes = [
      ...assetables_attributes.filter(assetable => assetable.asset_id !== assetId),
      {
        id: get(assetables_attributes.find(assetable => assetable.asset_id === assetId), 'id', null),
        asset_id: assetId,
        _destroy: selected
      }
    ]

    setFieldValue('assetables_attributes', newAssetableAttributes)
    setFieldValue('total_asset_monthly_contributions', sumSelectedAssetsContributions(assets, newAssetableAttributes, supportedCurrencies))
    setFieldValue('total_from_selected_assets', sumSelectedAssets(assets, newAssetableAttributes, supportedCurrencies))
    setFieldValue('total_from_selected_liabilities', sumSelectedLiabilities(liabilities, liabilitiables_attributes, assets, newAssetableAttributes))
  }
  return (
    <Grid mediumColumns>
      {map(assets, (asset, key) => {
        const selected = assetExistsInAssetables(asset, assetables_attributes)
        return (
          <AssetCard
            key={key}
            asset={asset}
            highlighted={selected}
            supportedCurrencies={supportedCurrencies}
            Footer={() =>
              <CardSelectionFooter
                onClick={onSelectAsset(asset.id, selected)}
                trackAs={'Fna: Select asset'}
                selected={selected}
              />
            }
          />
        )
      })}
    </Grid>
  )
}

export const DefaultAside = () =>
  <InfoSection>
    <h3>
      Don't just plan your financial future<br />
      <strong>Protect it.</strong>
    </h3>
    <img className='cashflow-dashboard-image' alt='cashflow-events' src={bannerImg} />
  </InfoSection>

export const CardSelectionFooter = ({ onClick, trackAs, selected }) =>
  <CardFooter>
    <Button
      textLink
      onClick={onClick}
      trackAs={trackAs}
    >
      {selected ? 'Remove' : 'Select'}
    </Button>
  </CardFooter>

export const assetExistsInAssetables = (asset, assetables_attributes) => {
  return assetables_attributes.some(assetable =>
    assetable.asset_id === asset.id && !assetable._destroy
  )
}

export const liabilityExistsInLiabilitiables = (liability, liabilitiables_attributes) =>
  liabilitiables_attributes.some(liabilitiable =>
    liabilitiable.liability_id === liability.id && !liabilitiable._destroy
  )

export const riskProductExistsInRiskProductables = (riskProduct, risk_productables_attributes) =>
  risk_productables_attributes.some(riskProductable =>
    riskProductable.risk_product_id === riskProduct.id && !riskProductable._destroy
  )

const AsideInfo = ({ title, Content }) => {
  return (
    <Box
      m='1rem'
    >
      <Box
        position={'sticky'}
        top='100px'
      >
        <h3>{title}</h3>
        <Content />
      </Box>
    </Box>
  )
}
